import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CopyAll, LogoutOutlined } from "@mui/icons-material";
import { Tables } from "../../../database.types";
import { StandardButton } from "../../components/StandardButton";

interface LeagueHeaderProps {
  league: Tables<"leagues">;
  isMobile: boolean;
  isEditingName: boolean;
  editedName: string;
  setEditedName: (name: string) => void;
  setIsEditingName: (editing: boolean) => void;
  onBackToLeagueSelection?: () => void;
  onSaveLeagueName: () => Promise<void>;
  onCopyJoinCode: () => Promise<void>;
  onSignOutClick: () => void;
}

export const LeagueHeader: React.FC<LeagueHeaderProps> = ({
  league,
  isMobile,
  isEditingName,
  editedName,
  setEditedName,
  setIsEditingName,
  onBackToLeagueSelection,
  onSaveLeagueName,
  onCopyJoinCode,
  onSignOutClick,
}) => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      flexDirection={isMobile ? "column" : "row"}
      gap={isMobile ? 2 : 0}
      mb={3}
    >
      <Box display="flex" alignItems="center" gap={2}>
        {onBackToLeagueSelection && (
          <StandardButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToLeagueSelection}
            size={isMobile ? "small" : "medium"}
            sx={{ color: "white", borderColor: "white" }}
          >
            {isMobile ? "" : "Leagues"}
          </StandardButton>
        )}
        {isEditingName ? (
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveLeagueName();
                if (e.key === "Escape") {
                  setEditedName(league.name);
                  setIsEditingName(false);
                }
              }}
              size="small"
              autoFocus
              sx={{
                "& .MuiInputBase-input": {
                  color: "white",
                  fontSize: "2.125rem",
                  fontWeight: "bold",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "white",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "white",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  { borderColor: "white" },
              }}
            />
            <IconButton onClick={onSaveLeagueName} sx={{ color: "white" }}>
              <CheckIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setEditedName(league.name);
                setIsEditingName(false);
              }}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              color="white"
            >
              {league.name || "League Dashboard"}
            </Typography>
            <IconButton
              onClick={() => setIsEditingName(true)}
              sx={{ color: "white" }}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={2}>
        <StandardButton
          variant="contained"
          startIcon={<CopyAll />}
          onClick={onCopyJoinCode}
          size={isMobile ? "small" : "medium"}
        >
          Copy League Join Code
        </StandardButton>
        <StandardButton
          variant="outlined"
          startIcon={<LogoutOutlined />}
          onClick={onSignOutClick}
          size={isMobile ? "small" : "medium"}
          sx={{ color: "white", borderColor: "white" }}
        >
          Sign Out
        </StandardButton>
      </Box>
    </Box>
  );
};
